import { useRef, useCallback } from "react";
import SplashCursor, { type SplashCursorHandle } from "./SplashCursor";

export default function Hero() {
  const splashRef = useRef<SplashCursorHandle>(null);
  // throttle so we don't fire a burst on every single mousemove pixel
  const lastBurstRef = useRef<number>(0);

  const handleBurst = useCallback((target: HTMLElement | null) => {
    if (!target) return;
    const now = performance.now();
    // fire at most ~6 bursts per second per element area
    if (now - lastBurstRef.current < 160) return;
    lastBurstRef.current = now;
    splashRef.current?.burst(target.getBoundingClientRect());
  }, []);

  return (
    <section className="relative w-full h-screen overflow-hidden bg-background-50 flex flex-col">
      {/* GPU fluid simulation background — boosted colors for cream canvas */}
      <div className="absolute inset-0 z-0">
        <SplashCursor
          ref={splashRef}
          DENSITY_DISSIPATION={2.2}
          VELOCITY_DISSIPATION={1.6}
          SPLAT_RADIUS={0.28}
          SPLAT_FORCE={7200}
          CURL={4}
          COLOR_UPDATE_SPEED={8}
          BACK_COLOR={{ r: 0, g: 0, b: 0 }}
        />
      </div>

      {/* very light cream tint so text reads cleanly over swirling fluid */}
      <div className="pointer-events-none absolute inset-0 z-[1] bg-background-50/55" />

      {/* top navigation */}
      <header className="relative z-20 w-full">
        <nav className="grid grid-cols-3 items-center px-6 md:px-14 py-7">
          <ul className="flex items-center gap-10 text-[15px] font-label text-foreground-900">
            <li>
              <a href="#listings" className="italic hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
                Listings
              </a>
            </li>
            <li>
              <a href="#sell" className="italic hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
                Sell
              </a>
            </li>
            <li>
              <a href="#about" className="italic hover:text-foreground-950 transition-colors cursor-pointer whitespace-nowrap">
                About
              </a>
            </li>
          </ul>

          <a href="/" className="flex flex-col items-center justify-center gap-1 group">
            <span className="w-10 h-10 flex items-center justify-center text-primary-900">
              <i className="ri-plant-fill text-2xl" />
            </span>
            <span className="font-heading text-[11px] font-medium tracking-wider text-foreground-900">
              Green Vale
            </span>
          </a>

          <div className="flex items-center justify-end">
            <a
              href="#contact"
              className="inline-flex items-center px-6 py-2.5 rounded-full border border-foreground-900/70 text-foreground-950 hover:bg-foreground-950 hover:text-background-50 text-[14px] font-label italic transition-colors cursor-pointer whitespace-nowrap"
            >
              Contact Us
            </a>
          </div>
        </nav>
      </header>

      {/* hero headline block */}
      <div className="relative z-10 flex flex-col items-center px-6 md:px-10 text-center pt-1">
        <h1 className="font-heading font-black tracking-[0.01em] text-foreground-950 leading-[0.9] whitespace-nowrap text-[12vw] xl:text-[11rem] flex items-baseline justify-center gap-[0.6em]">
          <span>GREEN</span>
          <span>VALE</span>
        </h1>

        <p className="mt-3 font-heading italic text-foreground-800 text-lg md:text-xl">
          Your Next Home is Here
        </p>
      </div>

      {/* bottom row — image left, who-we-are text right */}
      <div className="relative z-10 mx-auto w-full max-w-[1200px] px-6 md:px-10 mt-auto pb-8 md:pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-[1.05fr_1fr] gap-8 lg:gap-14 items-start">
          {/* left image — taller now, with hover-burst */}
          <div
            className="relative w-full h-[48vh] md:h-[58vh] overflow-hidden rounded-md transition-transform duration-300 ease-out hover:-translate-y-1"
            onMouseEnter={(e) => handleBurst(e.currentTarget)}
            onMouseMove={(e) => handleBurst(e.currentTarget)}
          >
            <img
              src="https://readdy.ai/api/search-image?query=Sun-drenched%20Scandinavian%20industrial%20loft%20interior%20with%20tall%20black%20steel%20factory%20windows%2C%20warm%20wooden%20floorboards%2C%20a%20curved%20cream%20lounge%20chair%20with%20ottoman%2C%20floating%20wood%20shelves%20displaying%20colorful%20art%20books%20and%20pottery%2C%20framed%20abstract%20paintings%20in%20red%20yellow%20and%20blue%20on%20white%20plaster%20walls%2C%20lush%20potted%20monstera%20and%20snake%20plants%2C%20golden%20morning%20sunlight%20streaming%20across%20the%20space%2C%20editorial%20architectural%20photography%20with%20soft%20natural%20shadows%20and%20a%20calm%20warm%20cream%20background%20tone&width=1200&height=1600&seq=greenvale-loft-02&orientation=portrait"
              alt="Sunlit loft interior of a Green Vale listed home"
              title="Green Vale loft listing interior"
              className="w-full h-full object-cover object-top pointer-events-none"
            />
          </div>

          {/* right text — also reactive to hover */}
          <div
            className="flex flex-col"
            onMouseEnter={(e) => handleBurst(e.currentTarget)}
            onMouseMove={(e) => handleBurst(e.currentTarget)}
          >
            {/* secondary image above WHO WE ARE */}
            <div className="relative w-full h-[28vh] md:h-[32vh] overflow-hidden rounded-md mb-5 transition-transform duration-300 ease-out hover:-translate-y-1">
              <img
                src="https://readdy.ai/api/search-image?query=Bright%20warm%20Californian%20modern%20kitchen%20interior%20with%20pale%20oak%20cabinetry%2C%20a%20creamy%20marble%20island%20countertop%2C%20brass%20pendant%20lights%2C%20a%20bowl%20of%20fresh%20lemons%2C%20a%20vase%20of%20wildflowers%2C%20open%20wooden%20shelves%20with%20ceramic%20mugs%20and%20cookbooks%2C%20arched%20doorway%2C%20soft%20late%20afternoon%20sunlight%20pouring%20through%20linen%20curtains%2C%20warm%20cream%20and%20butter%20color%20palette%2C%20editorial%20interior%20photography%2C%20natural%20organic%20composition%20with%20a%20calm%20background%20tone&width=1400&height=900&seq=greenvale-kitchen-01&orientation=landscape"
                alt="Sunlit modern kitchen interior of a Green Vale listed home"
                title="Green Vale kitchen listing interior"
                className="w-full h-full object-cover object-top pointer-events-none"
              />
            </div>

            <h2 className="font-heading font-black tracking-[-0.02em] text-foreground-950 text-4xl md:text-5xl leading-[0.95]">
              WHO WE ARE
            </h2>

            <p className="mt-4 font-body text-[14px] md:text-[15px] text-foreground-700 leading-[1.7] max-w-[460px]">
              Since 2010, we have dedicated ourselves to making moving a joyful
              experience within the San Francisco community we serve. We strive
              to provide a service that is both highly professional and
              genuinely caring, ensuring a more personal touch in every
              interaction.
            </p>

            <div className="mt-5">
              <a
                href="#about-us"
                className="inline-flex items-center justify-center px-7 py-2.5 rounded-full bg-foreground-950 text-background-50 hover:bg-foreground-800 text-[13px] font-label italic transition-colors cursor-pointer whitespace-nowrap"
              >
                About Us
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}